import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select, { components } from "react-select";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";
import ReactDOM from "react-dom";

import { useFormInput, comma, formatDate } from "@/utils";
import { Confirm, Loading, Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import { useDeleteCarMutation, useLazySearchCarQuery, usePostCarMutation, usePutCarMutation } from "@/redux/carApi";
import { useGetBranchInfoQuery, useGetMyRentCorpQuery, useSetRentCorpPrimaryMutation } from "@/redux/rentCorpApi";
import { useGetCarLocationListQuery } from "@/redux/carLocationApi";
import { usePostParkingLotMutation } from "@/redux/parkingLotApi";
import { rolesOptions, memberStatusOptions, corpTypeOptions, branchTypeOptions } from "@/constants/selectOptions";
import { useGetBankListQuery, useGetAddressCodeQuery, useGetAddressCode2Mutation } from "@/redux/metaApi";
import {
    useGetMemberDetailQuery,
    useMemberResetPasswordMutation,
    useUpdateMemberMutation,
    useDeleteMemberMutation,
} from "@/redux/adminMemberApi";
import { useAuthChangePasswordMutation } from "@/redux/authApi";
import { FormAccident, FormEmployees, FormInsurance, FormMechanic, FormPartner, FeeInfo } from "@/pages/ERP/AdminMemberOrderDetailForms";
import { formatPhoneNumber } from "../../utils";

// 역할 우선순위 정의
const ROLE_PRIORITY = ["NORMAL_PARTNER", "MECHANIC_PARTNER", "INSURANCE_EMP", "INSURANCE_SERVICE", "SALES_PARTNER", "CONTRACT_EMP"];

// 비밀번호 초기화 팝업 컴포넌트
const ResetPasswordPopup = ({ onClose, memberId }) => {
    const [authChangePassword] = useAuthChangePasswordMutation();

    const { formData, handleChange, setFormData } = useFormInput({
        toastMsg: "",
        password: "",
        passwordCheck: "",
    });

    useEffect(() => {
        document.body.classList.add("scroll-lock");
        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    const validatePassword = (password, passwordCheck) => {
        if (!password) return "비밀번호를 입력해주세요.";
        if (password !== passwordCheck) return "비밀번호가 일치하지 않습니다.";

        const hasLetter = /[a-zA-Z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < 8 || !hasLetter || !hasNumber || !hasSpecial) {
            return "비밀번호는 영문, 숫자, 특수문자를 모두 포함한 8자 이상으로 설정해주세요.";
        }
        return "";
    };

    const handleResetPassword = async () => {
        const errMsg = validatePassword(formData.password, formData.passwordCheck);
        if (errMsg) {
            setFormData((prev) => ({ ...prev, toastMsg: errMsg }));
            return;
        }

        try {
            await authChangePassword({ memberId, password: formData.password }).unwrap();
            setFormData((prev) => ({
                ...prev,
                toastMsg: "비밀번호가 성공적으로 변경되었습니다.",
            }));
            setTimeout(() => onClose(), 1500);
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "비밀번호 변경에 실패했습니다.",
            }));
        }
    };

    return ReactDOM.createPortal(
        <>
            {formData.toastMsg && <Toast visible={true} msg={formData.toastMsg} setFormData={setFormData} />}

            <div className="layer active" onClick={onClose}></div>

            <div className="popup input-popup">
                <div className="popup-content">
                    <div className="content-title">
                        <span>비밀번호 초기화</span>
                    </div>
                    <div className="content-description">
                        <p>새로운 비밀번호를 입력해주세요.</p>
                    </div>
                    <div className="content-inputs">
                        <div className="input-group">
                            <label>새 비밀번호</label>
                            <div className="input-wrapper">
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="새 비밀번호 입력"
                                />
                            </div>
                        </div>
                        <div className="input-group">
                            <label>비밀번호 확인</label>
                            <div className="input-wrapper">
                                <input
                                    type="password"
                                    name="passwordCheck"
                                    value={formData.passwordCheck}
                                    onChange={handleChange}
                                    placeholder="비밀번호 확인"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleResetPassword}
                        disabled={!formData.password || !formData.passwordCheck}
                    >
                        비밀번호 변경
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};

export const AdminMemberOrderDetail = () => {
    const { memberId } = useParams();
    const navigate = useNavigate();
    const [resetPasswordPopupVisible, setResetPasswordPopupVisible] = useState(false);
    const [isDetailLoading, setIsDetailLoading] = useState(false);

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE", // "CREATE" / "READ" / "UPDATE"
        isLoading: false,
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        id: null,
        member: {},
        rentCorps: [],
        selectedRentCorp: {},
    });
    const { mode, isLoading, toastMsg, confirmStatus, addressPopupStatus, id } = formData;

    const { data: memberDetailData, refetch: refetchMemberDetail } = useGetMemberDetailQuery(memberId, {
        skip: !memberId,
    });

    useEffect(() => {
        const role = rolesOptions?.find((role) => memberDetailData?.data?.member?.roles?.includes(role.value));
        setFormData((prev) => ({
            ...prev,
            role: role?.value,
        }));
    }, [memberDetailData]);

    useEffect(() => {
        console.log(formData);
    }, [formData]);

    const [updateMember] = useUpdateMemberMutation();
    const [memberResetPassword] = useMemberResetPasswordMutation();

    const fetchMemberDetail = async (memberId) => {
        setIsDetailLoading(true);
        try {
            const response = await refetchMemberDetail();
            setFormData((prev) => ({
                ...prev,
                member: response.data.data.member,
                rentCorps: response.data.data.rentCorps,
                selectedRentCorp: response.data.data.rentCorps?.length > 0 ? response.data.data.rentCorps[0] : {},
                rentCorpAreas: response.data.data.rentCorpAreas || [],
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "회원 정보 조회에 실패했습니다.",
            }));
        } finally {
            setIsDetailLoading(false);
        }
    };

    // 모드 설정: memberId 있으면 "UPDATE"
    useEffect(() => {
        if (memberId) {
            fetchMemberDetail(memberId);
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: memberId,
            }));
        }
    }, [memberId, setFormData]);

    // API 호출
    const [postCar, { isLoading: isPostLoading }] = usePostCarMutation();
    const [putCar, { isLoading: isPutLoading }] = usePutCarMutation();
    const [postParkingLot] = usePostParkingLotMutation();
    const [doSearchCar, { data: searchCarData, isFetching, error }] = useLazySearchCarQuery();
    const [deleteMember] = useDeleteMemberMutation();

    const handleDeleteConfirm = async () => {
        if (!formData?.id) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "삭제할 회원이 없습니다.",
            }));
            return;
        }

        try {
            await deleteMember(formData?.id).unwrap();

            setFormData((prev) => ({
                ...prev,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: "회원 정보 삭제",
                    confirmDescription: "회원이 성공적으로 삭제되었습니다.",
                    confirmButtons: [
                        {
                            label: "확인",
                            className: "btn-primary",
                            onClick: () => {
                                navigate("/erp/member/manage");
                            },
                        },
                    ],
                },
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    const handleDelete = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `${formData.member.name} 회원님의 모든 데이터를 삭제하시겠습니까?`,
                confirmDescription: "삭제 후에는 복구할 수 없습니다.",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    useEffect(() => {
        if (memberId) {
            setFormData((prev) => ({
                ...prev,
                mode: "READ",
                id: memberId,
            }));
        }
    }, [memberId, setFormData]);

    const handleSave = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "회원 정보 수정",
                confirmDescription: "회원 정보를 수정하시겠습니까?",
                confirmButtons: [
                    {
                        label: "취소",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev) => ({
                                ...prev,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                    {
                        label: "확인",
                        className: "btn-primary",
                        onClick: async () => {
                            try {
                                let requestBody = {
                                    loginId: formData.member?.loginId,
                                    idNumber: formData.member?.idNumber,
                                    name: formData.member?.name,
                                    phone: formData.member?.phone,
                                    roles: formData.member?.roles || ["MASTER"],
                                    rentCorp: {
                                        id: formData.selectedRentCorp?.id,
                                        corpName: formData.selectedRentCorp?.corpName,
                                        bankName: formData.selectedRentCorp?.bankName,
                                        bankAccount: formData.selectedRentCorp?.bankAccount,
                                        bankOwner: formData.selectedRentCorp?.bankOwner,
                                    },
                                    memo: formData.memo,
                                    firstAddressCodeId: formData.member?.firstAddressCodeId,
                                    secondAddressCodeId: formData.member?.secondAddressCodeId,
                                    branchType: formData.member?.branchType,
                                };

                                const matchedRole = ROLE_PRIORITY.find((r) => formData.member?.roles?.includes(r));
                                console.log(matchedRole);
                                // orderJoinType에 따라 추가 정보 병합
                                switch (matchedRole) {
                                    case "MECHANIC_PARTNER":
                                        requestBody = { ...requestBody, mechanicInfo: formData.member?.mechanicInfo };
                                        break;
                                    case "INSURANCE_EMP":
                                        requestBody = { ...requestBody, insuranceCorpInfo: formData.member?.insuranceCorpInfo };
                                        break;
                                    case "INSURANCE_SERVICE":
                                        requestBody = { ...requestBody, insuranceSvcInfo: formData.member?.insuranceSvcInfo };
                                        break;
                                    case "SALES_PARTNER":
                                        requestBody = { ...requestBody, salesPartnerInfo: formData.member?.salesPartnerInfo };
                                        break;
                                    case "CONTRACT_EMP":
                                        requestBody = { ...requestBody, contractPartnerInfo: formData.member?.contractPartnerInfo };
                                        break;
                                    default:
                                        break;
                                }
                                console.log("Request payload:", { memberId, requestBody });

                                await updateMember({ memberId, body: requestBody }).unwrap();

                                setFormData((prev) => ({
                                    ...prev,
                                    toastMsg: "회원 정보가 성공적으로 수정되었습니다.",
                                    confirmStatus: { confirmPopupStatus: false },
                                }));
                            } catch (error) {
                                console.error("API Error:", error);
                                setFormData((prev) => ({
                                    ...prev,
                                    toastMsg: error?.message || "회원 정보 수정에 실패했습니다.",
                                    confirmStatus: { confirmPopupStatus: false },
                                }));
                            }
                        },
                    },
                ],
            },
        }));
    };

    const handleResetPassword = async () => {
        try {
            await memberResetPassword(memberId).unwrap();
            setFormData((prev) => ({
                ...prev,
                toastMsg: "비밀번호가 성공적으로 초기화되었습니다.",
            }));
            setResetPasswordPopupVisible(false);
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "비밀번호 초기화에 실패했습니다.",
            }));
        }
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">{mode === "CREATE" ? "오더 회원 등록" : "오더 회원 상세"}</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            <button type="button" className="btn btn-outline" onClick={() => navigate(-1)}>
                                이전으로
                            </button>
                            {mode !== "CREATE" && (
                                <>
                                    <button type="button" className="btn btn-secondary" onClick={() => setResetPasswordPopupVisible(true)}>
                                        비밀번호 초기화
                                    </button>
                                </>
                            )}
                            <button type="button" className="btn btn-cancel" onClick={handleDelete}>
                                회원 삭제
                            </button>
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                저장
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">기본 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <MemberInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">추가 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        {formData.role === "MECHANIC_PARTNER" && (
                                            <FormMechanic formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                        )}
                                        {formData.role === "INSURANCE_EMP" && (
                                            <FormInsurance formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                        )}
                                        {formData.role === "INSURANCE_SERVICE" && (
                                            <FormAccident formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                        )}
                                        {formData.role === "SALES_PARTNER" && (
                                            <FormPartner formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                        )}
                                        {formData.role === "CONTRACT_EMP" && (
                                            <FormEmployees formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {formData.role === "CONTRACT_EMP" && (
                                <div className="form-item">
                                    <div className="item-head">
                                        <span className="form-item_tit">수수료 설정</span>
                                        <div className="form-item_etc"></div>
                                    </div>
                                    <div className="item-body">
                                        <div className="item-list">
                                            <FeeInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">메모사항</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <MemoInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            parkingLotAddress: data.address,
                            parkingLotBCode: data.bcode,
                        }))
                    }
                />
            )}

            {/* 비밀번호 초기화 팝업 */}
            {resetPasswordPopupVisible && <ResetPasswordPopup onClose={() => setResetPasswordPopupVisible(false)} memberId={memberId} />}
        </>
    );
};

/* 기본 정보 */
const MemberInfo = ({ formData, handleChange, setFormData, handleSearchCar }) => {
    const { ownerName, vehicleOrigin, color, roles } = formData;

    return (
        <>
            {/* 로그인 ID */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>로그인 ID</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="member.loginId"
                            placeholder=""
                            value={formData.member?.loginId || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            {/* 회원상태 선택 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>회원상태</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={memberStatusOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder=""
                            value={memberStatusOptions.find((opt) => opt.value === formData.member?.memberStatus)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, vehicleOrigin: option.value }))}
                            isDisabled
                        />
                    </div>
                </div>
            </div>

            {/* 포인트 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>마일리지</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="member.mileage"
                            placeholder=""
                            value={comma(formData.member?.mileage || "0")}
                            onChange={handleChange}
                            disabled
                        />
                    </div>
                </div>
            </div>

            {/* 이름 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>이름</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="member.name" placeholder="" value={formData.member?.name || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 연락처 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="member.phone"
                            placeholder=""
                            value={formatPhoneNumber(formData.member?.phone || "")}
                            onChange={(e) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    member: {
                                        ...prev.member,
                                        phone: e.target.value.replaceAll("-", ""),
                                    }
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 주민등록번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>주민등록번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="member.idNumber"
                            placeholder=""
                            value={formData.member?.idNumber || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            {/* 권한 설정 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>권한 설정</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            isMulti
                            options={rolesOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="권한 선택"
                            value={rolesOptions.filter((option) => formData.member?.roles?.includes(option.value))}
                            onChange={(selectedOptions) => {
                                const newRoles = selectedOptions ? selectedOptions.map((option) => option.value) : [];
                                setFormData((prev) => ({
                                    ...prev,
                                    member: {
                                        ...prev.member,
                                        roles: newRoles,
                                    },
                                }));
                            }}
                            styles={{
                                container: (provided) => ({
                                    ...provided,
                                }),
                                control: (provided) => ({
                                    ...provided,
                                    height: "unset !important",
                                }),
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const MemoInfo = ({ formData, handleChange, setFormData }) => {
    const { memo } = formData;

    return (
        <>
            {/* 메모 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>메모</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea name="memo" placeholder="메모를 작성해주세요." value={memo} onChange={handleChange} />
                    </div>
                </div>
            </div>
        </>
    );
};
